import type { TrackEventConfig } from '../types';
import { visibleText } from './dom';

/**
 * Parse data-pt-track attribute value.
 *   Legacy:  "cta_click"
 *   New:     '{"eventName":"cta_click","properties":[...]}'
 */
export function parseTrackConfig(raw: string | null): TrackEventConfig | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && parsed.eventName) {
      return parsed as TrackEventConfig;
    }
  } catch {
    // Legacy string format
  }
  return { eventName: raw, properties: [] };
}

/**
 * Resolve a dynamic variable to its runtime value.
 *
 * The supported variable IDs MUST stay aligned with apps/x
 * trackEventResolver.ts. Adding here without adding there silently returns ''.
 */
export function resolveVariable(varId: string, element: Element): string {
  if (!varId) return '';
  switch (varId) {
    case 'element.text':
      // 排除发布生成的隐形 SEO 锚点文本（见 utils/dom.ts visibleText）。这条必须与
      // getTextContent 同步 —— 它是独立实现，而 iam 自动配的 button 埋点正是用本变量，
      // 漏改这一处等于只修了一半（自动补的 label 干净了、显式配的属性还是脏的）。
      return visibleText(element).trim();
    case 'element.href':
      // data-pt-href = current click-action contract; bare href = legacy pages / native <a>
      return element.getAttribute('data-pt-href') || element.getAttribute('href') || '';
    case 'page.url':
      return location.href;
    case 'page.title':
      return document.title;
    case 'timestamp':
      return String(Date.now());
    default:
      if (varId.startsWith('url.param:')) {
        return new URLSearchParams(location.search).get(varId.slice(10)) || '';
      }
      if (varId.startsWith('form.field:')) {
        const input = document.querySelector<HTMLInputElement>(`[name="${varId.slice(11)}"]`);
        return input ? (input.value || '') : '';
      }
      if (varId.startsWith('cookie:')) {
        const key = varId.slice(7);
        const re = new RegExp('(?:^|; )' + key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)');
        const m = typeof document.cookie === 'string' ? document.cookie.match(re) : null;
        return m ? decodeURIComponent(m[1]) : '';
      }
      if (varId.startsWith('dataset:')) {
        const key = varId.slice(8);
        // closest [data-<key>] up the tree, so the editor can place it on a wrapper
        const el = (element as HTMLElement).closest('[data-' + cssEscapeDataKey(key) + ']');
        return el ? (el as HTMLElement).getAttribute('data-' + cssEscapeDataKey(key)) || '' : '';
      }
      return '';
  }
}

function cssEscapeDataKey(key: string): string {
  // dataset key camelCase → data-attribute kebab-case is handled by HTMLElement.dataset, but
  // for the closest() selector we accept the user-provided id as-is (it should already be the
  // hyphenated form), only escaping special chars.
  return key.replace(/[^a-zA-Z0-9_-]/g, '');
}

/**
 * Resolve all properties in a track config to concrete key-value pairs.
 */
export function resolveProperties(
  config: TrackEventConfig,
  element: Element,
): Record<string, string> {
  const result: Record<string, string> = {};
  for (const p of config.properties) {
    if (!p.key) continue;
    result[p.key] = p.valueType === 'variable'
      ? resolveVariable(p.value, element)
      : (p.value || '');
  }
  return result;
}
